import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Chip,
  Paper,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import WaitTimeDebug from '../debug/WaitTimeDebug';
import {
  DirectionsCar as VehiclesIcon,
  Person as DriversIcon,
  People as CustomersIcon,
  Route as RidesIcon,
  Schedule as ScheduleIcon,
  Timer as WaitTimeIcon,
  DirectionsCar as CarIcon,
  AirportShuttle as KombiIcon,
  ElectricRickshaw as MinibusIcon,
  LocalHospital as MedicalIcon,
  Accessible as WheelchairIcon,
  EventSeat as TransportChairIcon,
  Hotel as StretcherIcon
} from '@mui/icons-material';
import brandingConfig from '../../config/brandingConfig';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import LiveMap from './LiveMap';
import { onSnapshot, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';

// Normalisiert Firestore-Timestamps, {seconds}-Objekte und ISO-Strings zu ms.
// Rides aus dem AppDataManager enthalten rohe Firestore-Timestamps – ohne
// diesen Helper scheitern Sortierung und Tagesvergleich an `new Date(ts)` =
// Invalid Date.
const toMs = (t) => {
  if (!t) return null;
  if (t.toDate) return t.toDate().getTime();
  if (t.seconds) return t.seconds * 1000;
  const ms = new Date(t).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const StatsCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'accent',
})(({ theme, accent = brandingConfig.colors.primary }) => ({
  padding: theme.spacing(1.5),
  textAlign: 'left',
  borderRadius: theme.spacing(1),
  height: '100%',
  minHeight: '96px',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderLeft: `4px solid ${accent}`,
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
  transition: 'box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 6px 18px rgba(15, 23, 42, 0.12)',
    borderColor: `${accent}66`,
  },
}));

const StatIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'accent',
})(({ theme, accent = brandingConfig.colors.primary }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 38,
  height: 38,
  borderRadius: theme.spacing(1),
  backgroundColor: `${accent}18`,
  flexShrink: 0,
  '& .MuiSvgIcon-root': {
    fontSize: 22,
    color: accent,
  },
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.45rem',
  lineHeight: 1.15,
  fontWeight: 800,
  letterSpacing: 0,
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.78rem',
  lineHeight: 1.25,
  fontWeight: 600,
}));

const WaitTimePanel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1),
  border: '1px solid',
  borderColor: theme.palette.divider,
  background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
}));

// Optimierte und memoizierte UI-Komponenten für das Dashboard

// Diese Komponente wird nicht mehr benötigt, da wir die StatsCard direkt verwenden
// Die betreffende Komponente wurde entfernt, um Konflikte zu vermeiden

const Dashboard = () => {
  // Verwende neues AppData System (memoized selectors um Re-render zu vermeiden)
  const drivers = useSelector(state => state.appData.drivers);
  const driversLoading = useSelector(state => state.appData.isLoading.drivers);
  const driversError = useSelector(state => state.appData.error.drivers);
  
  const vehicles = useSelector(state => state.appData.vehicles);
  const vehiclesLoading = useSelector(state => state.appData.isLoading.vehicles);
  const vehiclesError = useSelector(state => state.appData.error.vehicles);
  
  const rides = useSelector(state => state.appData.rides);
  const ridesLoading = useSelector(state => state.appData.isLoading.rides);
  const ridesError = useSelector(state => state.appData.error.rides);
  
  const shifts = useSelector(state => state.appData.shifts);
  const shiftsLoading = useSelector(state => state.appData.isLoading.shifts);
  const shiftsError = useSelector(state => state.appData.error.shifts);
  
  const customers = useSelector(state => state.appData.customers);
  const customersLoading = useSelector(state => state.appData.isLoading.customers);
  const customersError = useSelector(state => state.appData.error.customers);
  
  // Firebase Wartezeiten direkt lesen (keine eigene Berechnung im Dashboard)
  const [waitTimes, setWaitTimes] = useState({
    general: "Berechnung...",
    grossraum: "Berechnung...",
    kleinbus7: "Berechnung...",
    kleinbus8: "Berechnung..."
  });
  const [nextUnavailable, setNextUnavailable] = useState(null);

  // Firebase Listener für Wartezeiten
  useEffect(() => {
    const waitTimeDocRef = doc(db, 'waitTimes', 'current');
    
    const unsubscribe = onSnapshot(waitTimeDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setWaitTimes({
          general: data.general || "Berechnung...",
          grossraum: data.grossraum || "Berechnung...",
          kleinbus7: data.kleinbus7 || "Berechnung...",
          kleinbus8: data.kleinbus8 || "Berechnung..."
        });
        setNextUnavailable(data.nextUnavailable || null);

        if (process.env.NODE_ENV === 'development') {
          console.log('Dashboard: Firebase Wartezeiten empfangen:', {
            general: data.general,
            grossraum: data.grossraum,
            kleinbus7: data.kleinbus7,
            kleinbus8: data.kleinbus8
          });
        }
      } else {
        // Fallback wenn keine Daten vorhanden
        setWaitTimes({
          general: "Keine Daten",
          grossraum: "Keine Daten",
          kleinbus7: "Keine Daten",
          kleinbus8: "Keine Daten"
        });
        setNextUnavailable(null);
      }
    }, (error) => {
      console.error('Dashboard: Fehler beim Laden der Wartezeiten aus Firebase:', error);
      setWaitTimes({
        general: "Fehler",
        grossraum: "Fehler",
        kleinbus7: "Fehler",
        kleinbus8: "Fehler"
      });
    });

    return () => unsubscribe();
  }, []);
  
  // Überprüfen, ob Daten vom Redux-Store geladen werden
  const isReduxLoading = driversLoading || vehiclesLoading || ridesLoading || shiftsLoading || customersLoading;
  const hasError = driversError || vehiclesError || ridesError || shiftsError || customersError;

  // Ladezustand – AppDataManager übernimmt alles
  const [isManualLoading, setIsManualLoading] = useState(true);
  const isLoading = isReduxLoading || isManualLoading;

  useEffect(() => {
    if (!isReduxLoading) {
      setIsManualLoading(false);
    }
  }, [isReduxLoading]);

  // Stats als useMemo – kein useEffect+setState-Umweg mehr, kein Extra-Render-Zyklus
  const stats = useMemo(() => {
    if (isLoading || hasError) return {
      totalDrivers: 0, availableDrivers: 0, onlineDrivers: 0, onlineCustomers: 0,
      totalVehicles: 0, activeVehicles: 0, availableVehicles: 0, inUseVehicles: 0,
      vehicleTypes: { pkw: 0, grossraum: 0, kleinbus7: 0, kleinbus8: 0 },
      ridesByStatus: {}, totalShiftsToday: 0, preOrdersToday: 0, totalPreOrders: 0,
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayMs = today.getTime();
    const DONE = ['completed', 'cancelled', 'cancelled_by_driver'];

    const ridesByStatus = rides.reduce((acc, ride) => {
      acc[ride.status] = (acc[ride.status] || 0) + 1;
      return acc;
    }, {});

    const vehicleTypes = vehicles.reduce((acc, vehicle) => {
      if (vehicle.vehicleType === 1) acc.pkw += 1;
      else if (vehicle.vehicleType === 2) acc.grossraum += 1;
      else if (vehicle.vehicleType === 3) acc.kleinbus7 += 1;
      else if (vehicle.vehicleType === 4) acc.kleinbus8 += 1;
      return acc;
    }, { pkw: 0, grossraum: 0, kleinbus7: 0, kleinbus8: 0 });

    const totalPreOrders = rides.filter(r => r.orderType === 1 && !DONE.includes(r.status)).length;
    const preOrdersToday = rides.filter(ride => {
      if (ride.orderType !== 1 || DONE.includes(ride.status) || !ride.scheduledTime) return false;
      const ms = toMs(ride.scheduledTime);
      if (ms === null) return false;
      const d = new Date(ms);
      d.setHours(0, 0, 0, 0);
      return d.getTime() === todayMs;
    }).length;

    return {
      totalDrivers: drivers.length,
      availableDrivers: drivers.filter(d => d.isAvailable).length,
      onlineDrivers: drivers.filter(d => d.isOnline).length,
      onlineCustomers: customers.filter(c => c.isOnline).length,
      totalVehicles: vehicles.length,
      activeVehicles: vehicles.length,
      availableVehicles: vehicles.filter(v => !v.inUse).length,
      inUseVehicles: vehicles.filter(v => v.inUse === true).length,
      vehicleTypes,
      ridesByStatus,
      totalShiftsToday: shifts?.drivers ? Object.keys(shifts.drivers).length : 0,
      preOrdersToday,
      totalPreOrders,
    };
  }, [drivers, vehicles, rides, shifts, customers, isLoading, hasError]);

  const getWaitTimeSeverity = (waitTime) => {
    const value = String(waitTime || '');
    if (value.includes("nicht verfügbar") ||
        value.includes("Keine Fahrer verfügbar") ||
        value.includes("30+") ||
        value.includes("Fehler")) return "error";
    if (value.includes("nach Ausfallzeit") ||
        value.startsWith("2") ||
        value.startsWith("3")) return "warning";
    return "success";
  };

  const waitTimeItems = useMemo(() => {
    const items = [
      { key: 'general', label: 'PKW', value: waitTimes.general, icon: <CarIcon /> },
    ];

    if (stats.vehicleTypes.grossraum > 0) {
      items.push({ key: 'grossraum', label: 'Großraum', value: waitTimes.grossraum, icon: <KombiIcon /> });
    }
    if (stats.vehicleTypes.kleinbus7 > 0) {
      items.push({ key: 'kleinbus7', label: 'Kleinbus 7', value: waitTimes.kleinbus7, icon: <MinibusIcon /> });
    }
    if (stats.vehicleTypes.kleinbus8 > 0) {
      items.push({ key: 'kleinbus8', label: 'Kleinbus 8', value: waitTimes.kleinbus8, icon: <MinibusIcon /> });
    }

    return items;
  }, [stats.vehicleTypes, waitTimes]);

  // Verfügbarkeit von Krankenfahrt-Spezialfahrzeugen (Rollstuhl/Tragestuhl/Liegend):
  // Pro Capability geprüft, ob mindestens ein registriertes Fahrzeug aktuell
  // einem online angemeldeten Fahrer zugewiesen ist. Pause/aktive Fahrt zählt
  // weiterhin als verfügbar, da der Fahrer im Dienst ist.
  const specialCapabilityItems = useMemo(() => {
    const driverById = drivers.reduce((map, d) => {
      if (d?.id) map[d.id] = d;
      return map;
    }, {});

    const checkCapability = (capability) => {
      let hasAny = false;
      let isAvailable = false;
      for (const vehicle of vehicles) {
        const caps = Array.isArray(vehicle.specialCapabilities) ? vehicle.specialCapabilities : [];
        if (!caps.includes(capability)) continue;
        hasAny = true;
        const driverId = vehicle.driverId || vehicle.current_driver?.id || vehicle.current_driver_id || null;
        const driver = driverId ? driverById[driverId] : null;
        if (driver && driver.isOnline) {
          isAvailable = true;
          break;
        }
      }
      return { hasAny, isAvailable };
    };

    const definitions = [
      { key: 'rollstuhl', label: 'Rollstuhl', icon: <WheelchairIcon /> },
      { key: 'tragestuhl', label: 'Tragestuhl', icon: <TransportChairIcon /> },
      { key: 'liegend', label: 'Liegend', icon: <StretcherIcon /> },
    ];

    return definitions
      .map((def) => ({ ...def, ...checkCapability(def.key) }))
      .filter((item) => item.hasAny);
  }, [drivers, vehicles]);

  if (isLoading) {
    return <Loading message="Dashboard wird geladen..." />;
  }
  
  if (hasError) {
    console.error("Dashboard Fehler:", { 
      driversError, 
      vehiclesError, 
      ridesError, 
      shiftsError 
    });
    return (
      <ErrorMessage
        title="Fehler beim Laden der Dashboard-Daten"
        message={`Beim Laden der Dashboard-Daten ist ein Fehler aufgetreten. 
          Bitte stellen Sie sicher, dass Sie mit dem Internet verbunden sind und Firebase korrekt konfiguriert ist. 
          Details: 
          ${driversError ? `Fahrer: ${typeof driversError === 'object' ? driversError.message || JSON.stringify(driversError) : driversError}` : ''} 
          ${vehiclesError ? `Fahrzeuge: ${typeof vehiclesError === 'object' ? vehiclesError.message || JSON.stringify(vehiclesError) : vehiclesError}` : ''} 
          ${ridesError ? `Fahrten: ${typeof ridesError === 'object' ? ridesError.message || JSON.stringify(ridesError) : ridesError}` : ''} 
          ${shiftsError ? `Schichten: ${typeof shiftsError === 'object' ? shiftsError.message || JSON.stringify(shiftsError) : shiftsError}` : ''}`}
        onRetry={() => setIsManualLoading(true)}
      />
    );
  }
  
  return (
    <Box className="dashboard-container" sx={{ flexGrow: 1, p: 0, maxWidth: '100%', width: '100%' }}>
      <Box sx={{ px: 3, pt: 2 }}>
        <PageHeader
          title="Dashboard"
          description={`Willkommen im ${brandingConfig.appName} Admin-Portal. Hier finden Sie eine Echtzeit-Übersicht über Aktivitäten, Live-Fahrerstandorte und anstehende Fahrten.`}
        />
      </Box>
      
      {/* Debug Panel - temporär deaktiviert für Performance */}
      {false && process.env.NODE_ENV === 'development' && (
        <WaitTimeDebug />
      )}
      
      <Box sx={{ px: 3, pt: 3 }}>
        <WaitTimePanel elevation={0}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 190 }}>
              <StatIcon accent="#1976d2" sx={{ width: 34, height: 34 }}>
                <WaitTimeIcon />
              </StatIcon>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  Aktuelle Wartezeiten
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Nach Fahrzeugtyp
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1 }}>
              {waitTimeItems.map((item) => {
                const severity = getWaitTimeSeverity(item.value);
                const softBg = severity === 'error'
                  ? 'rgba(211, 47, 47, 0.08)'
                  : severity === 'warning'
                    ? 'rgba(237, 108, 2, 0.08)'
                    : 'rgba(46, 125, 50, 0.08)';

                return (
                  <Chip
                    key={item.key}
                    icon={item.icon}
                    label={`${item.label} ${item.value || 'Berechnung...'}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 30,
                      borderColor: `${severity}.main`,
                      color: `${severity}.dark`,
                      bgcolor: softBg,
                      fontWeight: 700,
                      '& .MuiChip-icon': { color: `${severity}.dark` },
                    }}
                  />
                );
              })}

              {specialCapabilityItems.length > 0 && (
                <MedicalIcon
                  fontSize="small"
                  sx={{ color: 'error.main', ml: 0.5 }}
                  titleAccess="Krankenfahrt-Spezialfahrzeuge"
                />
              )}
              {specialCapabilityItems.map((item) => {
                const severity = item.isAvailable ? 'success' : 'error';
                const softBg = item.isAvailable
                  ? 'rgba(46, 125, 50, 0.08)'
                  : 'rgba(211, 47, 47, 0.08)';
                return (
                  <Chip
                    key={item.key}
                    icon={item.icon}
                    label={`${item.label} ${item.isAvailable ? 'Verfügbar' : 'Nicht verfügbar'}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 30,
                      borderColor: `${severity}.main`,
                      color: `${severity}.dark`,
                      bgcolor: softBg,
                      fontWeight: 700,
                      '& .MuiChip-icon': { color: `${severity}.dark` },
                    }}
                  />
                );
              })}
            </Box>

            {nextUnavailable && (
              <Typography
                variant="caption"
                color="error.main"
                sx={{ fontWeight: 700, width: { xs: '100%', lg: 'auto' } }}
              >
                Keine Fahrer verfügbar in {nextUnavailable.minutesUntil} Min. für {nextUnavailable.duration} Min.
              </Typography>
            )}
          </Box>
        </WaitTimePanel>
      </Box>

      <Box sx={{ px: 3, pt: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
              lg: 'repeat(5, minmax(0, 1fr))',
            },
            gap: 1.5,
          }}
        >
          <StatsCard elevation={0} accent="#1976d2">
            <StatIcon accent="#1976d2">
              <DriversIcon />
            </StatIcon>
            <Box sx={{ minWidth: 0 }}>
              <StatValue>{`${stats.onlineDrivers} / ${stats.totalDrivers}`}</StatValue>
              <StatLabel>Fahrer online</StatLabel>
              <Typography variant="caption" color="text.secondary">
                {stats.availableDrivers} verfügbar
              </Typography>
            </Box>
          </StatsCard>

          <StatsCard elevation={0} accent="#2e7d32">
            <StatIcon accent="#2e7d32">
              <VehiclesIcon />
            </StatIcon>
            <Box sx={{ minWidth: 0 }}>
              <StatValue>{`${stats.inUseVehicles || 0} / ${stats.totalVehicles || 0}`}</StatValue>
              <StatLabel>Fahrzeuge im Einsatz</StatLabel>
              <Typography variant="caption" color="text.secondary">
                {stats.availableVehicles || 0} verfügbar
              </Typography>
            </Box>
          </StatsCard>

          <StatsCard elevation={0} accent="#ed6c02">
            <StatIcon accent="#ed6c02">
              <RidesIcon />
            </StatIcon>
            <Box sx={{ minWidth: 0 }}>
              <StatValue>
                {(stats.ridesByStatus.open || 0) +
                  (stats.ridesByStatus.accepted || 0) +
                  (stats.ridesByStatus.in_progress || 0) +
                  (stats.ridesByStatus.arrived || 0)}
              </StatValue>
              <StatLabel>Aktive Fahrten</StatLabel>
              <Typography variant="caption" color="text.secondary">
                Offen, aktiv oder unterwegs
              </Typography>
            </Box>
          </StatsCard>

          <StatsCard elevation={0} accent="#0288d1">
            <StatIcon accent="#0288d1">
              <ScheduleIcon />
            </StatIcon>
            <Box sx={{ minWidth: 0 }}>
              <StatValue>{`${stats.preOrdersToday} / ${stats.totalPreOrders}`}</StatValue>
              <StatLabel>Vorbestellungen</StatLabel>
              <Typography variant="caption" color="text.secondary">
                Heute / gesamt offen
              </Typography>
            </Box>
          </StatsCard>

          <StatsCard elevation={0} accent="#7b1fa2">
            <StatIcon accent="#7b1fa2">
              <CustomersIcon />
            </StatIcon>
            <Box sx={{ minWidth: 0 }}>
              <StatValue>{stats.onlineCustomers}</StatValue>
              <StatLabel>Kunden online</StatLabel>
              <Typography variant="caption" color="text.secondary">
                Live verbunden
              </Typography>
            </Box>
          </StatsCard>
        </Box>
      </Box>
      
      {/* Live Map - Full Width */}
      <Box className="full-width-container" sx={{ width: '100vw', maxWidth: '100%', mt: 4, p: 0, pb: 4 }}>
        <LiveMap />
      </Box>
    </Box>
  );
};

export default Dashboard;
